import React from 'react';

export type Theme = 'light' | 'dark';

export const ThemeContext = React.createContext<{
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
}>({ theme: 'light', toggleTheme: () => {}, setTheme: () => {} });

export const ThemeProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [theme, setThemeState] = React.useState<Theme>(() => {
    try {
      // Only read localStorage in a browser environment
      if (typeof window !== 'undefined') {
        const saved = localStorage.getItem('safonova_theme');
        if (saved === 'dark' || saved === 'light') return saved;
      }
    } catch (e) {
      // ignore
    }
    // Default to dark when no preference saved
    return 'dark';
  });

  const apply = React.useCallback((t: Theme) => {
    const root = document.documentElement;
    if (!root) return;
    if (t === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    try { localStorage.setItem('safonova_theme', t); } catch (e) {}
  }, []);

  React.useEffect(() => {
    apply(theme);
  }, [theme, apply]);

  const toggleTheme = React.useCallback(() => {
    setThemeState((s) => (s === 'dark' ? 'light' : 'dark'));
  }, []);

  const setTheme = React.useCallback((t: Theme) => setThemeState(t), []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
